import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { AppointmentAPI } from "../../services/appointment.service";
import type { AppointmentListItem, AppointmentSlot } from "../../services/appointment.service";
import { DoctorAPI } from "../../services/doctor.service";
import type { DoctorListItem } from "../../services/doctor.service";
import { PatientAPI } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import { AppointmentStatusBadge } from "../../components/appointments/AppointmentStatusBadge";
import { CancelAppointmentModal } from "../../components/appointments/CancelAppointmentModal";
import { ScanAndShareModal } from "../../components/abdm/ScanAndShareModal";
import { ScanArrivalCardStack } from "../../components/abdm/ScanArrivalCard";
import { useAbdmScanCards } from "../../hooks/useAbdmScanCards";
import { apiRequest } from "../../services/api.client";
import { QueueAPI } from "../../services/queue.service";
import type { QueueEntryListItem } from "../../services/queue.service";
import { QueueRow } from "../../components/queue/QueueRow";
import { RegisterWalkinModal } from "../../components/queue/RegisterWalkinModal";
import { useRole } from "../../hooks/useRole";

// ── Helpers ───────────────────────────────────────────────────────────────────
const toDateStr = (d: Date) => d.toISOString().slice(0, 10);

const fmt = (iso?: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const fmtLong = (d: Date) =>
  d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

// ── Types ─────────────────────────────────────────────────────────────────────
type ScheduledItem = AppointmentListItem & {
  slot_start: string;
  slot_end: string;
  doctor_name: string;
  patient_name: string;
  patient_display_id?: string;
};

// ── Scheduled appointment row (unchanged from the pre-walk-in page) ───────────
const ROW_BORDER: Record<string, string> = {
  scheduled:   "border-l-blue-400",
  confirmed:   "border-l-cyan-400",
  in_progress: "border-l-amber-400",
  completed:   "border-l-green-400",
  cancelled:   "border-l-slate-300",
  no_show:     "border-l-red-400",
};

const ScheduledRow: React.FC<{
  item: ScheduledItem;
  queueNum: number;
  onRefresh: () => void;
}> = ({ item, queueNum, onRefresh }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  // Confirm / Check In / Cancel all change the appointment (appointments.manage
  // server-side). A View Appointments-only role keeps the row and View link.
  const canManage = useRole().canOr("appointments.manage", true);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try {
      await fn();
      toast.success(`${label} successful`);
      onRefresh();
    } catch (e: any) {
      toast.error(e.message || `Failed: ${label}`);
    } finally {
      setBusy(null);
    }
  };

  const isActive = !["completed", "cancelled", "no_show"].includes(item.status);
  const borderCls = ROW_BORDER[item.status] ?? "border-l-slate-300";

  return (
    <>
      <div className={`bg-white rounded-2xl border border-slate-100 border-l-4 ${borderCls} shadow-sm px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 hover:shadow-md transition-shadow`}>
        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-slate-500">{queueNum}</span>
        </div>

        <div className="w-20 flex-shrink-0 text-center">
          <div className="text-sm font-bold text-slate-800">{fmt(item.slot_start)}</div>
          <div className="text-xs text-slate-400">{fmt(item.slot_end)}</div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800 truncate">{item.patient_name}</span>
            {item.patient_display_id && (
              <span className="text-[11px] text-slate-400 font-mono">{item.patient_display_id}</span>
            )}
            <AppointmentStatusBadge status={item.status} size="sm" />
          </div>
          <div className="text-xs text-slate-400 mt-0.5 truncate">{item.doctor_name}</div>
          {item.arrived_at && (
            <div className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
              Arrived {fmt(item.arrived_at)}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          {canManage && item.status === "scheduled" && (
            <button
              onClick={() => act("Confirmed", () => AppointmentAPI.confirm(item.appointment_id))}
              disabled={!!busy}
              className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-40 transition"
            >
              {busy === "Confirmed" ? "…" : "Confirm"}
            </button>
          )}

          {canManage && item.status === "confirmed" && (
            <button
              onClick={() => act("Checked in", () => AppointmentAPI.checkin(item.appointment_id))}
              disabled={!!busy}
              className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition"
            >
              {busy === "Checked in" ? "…" : "Check In"}
            </button>
          )}

          {canManage && isActive && (
            <button
              onClick={() => setShowCancel(true)}
              disabled={!!busy}
              className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-slate-100 text-slate-500 hover:bg-slate-200 disabled:opacity-40 transition"
            >
              Cancel
            </button>
          )}

          <Link
            to={`/appointments/confirmation/${item.appointment_id}`}
            className="inline-flex items-center justify-center px-3 min-h-[44px] rounded-lg text-xs font-medium bg-slate-100 text-slate-500 hover:bg-slate-200 transition"
          >
            View
          </Link>
        </div>
      </div>

      <CancelAppointmentModal
        isOpen={showCancel}
        appointmentId={item.appointment_id}
        onClose={() => setShowCancel(false)}
        onSuccess={() => { setShowCancel(false); onRefresh(); }}
      />
    </>
  );
};

// ── One doctor's walk-in queue: its own drag-drop context ─────────────────────
const DoctorWalkinQueue: React.FC<{
  doctorName: string;
  entries: QueueEntryListItem[];
  queueDate: string;
  onRefresh: () => void;
}> = ({ doctorName, entries, queueDate, onRefresh }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const waitingIds = useMemo(
    () => entries.filter((e) => e.state === "waiting").map((e) => e.queue_entry_id),
    [entries],
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = waitingIds.indexOf(String(active.id));
    const newIndex = waitingIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(waitingIds, oldIndex, newIndex);
    try {
      await QueueAPI.reorder({
        doctor_id: entries[0].doctor_id,
        queue_date: queueDate,
        ordered_queue_entry_ids: reordered,
      });
      onRefresh();
    } catch (e: any) {
      toast.error(e.message || "Failed to reorder queue");
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-slate-600 px-1">{doctorName}</h3>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={waitingIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {entries.map((entry, idx) => (
              <QueueRow key={entry.queue_entry_id} entry={entry} queueNum={idx + 1} onRefresh={onRefresh} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const ReceptionistQueuePage: React.FC = () => {
  const today = toDateStr(new Date());

  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [patients, setPatients] = useState<PatientDbRecord[]>([]);
  const [queueByDoctor, setQueueByDoctor] = useState<Record<string, QueueEntryListItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [hideTerminal, setHideTerminal] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [abdmEnabled, setAbdmEnabled] = useState<boolean | null>(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showRegisterWalkin, setShowRegisterWalkin] = useState(false);
  const { cards: arrivalCards, dismiss: dismissArrivalCard } = useAbdmScanCards();

  useEffect(() => {
    apiRequest<{ success: boolean }>("/abdm/link/generate-qr", { method: "POST" })
      .then(() => setAbdmEnabled(true))
      .catch((e: any) => setAbdmEnabled(e?.status !== 402 ? true : false));
  }, []);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const isoDate = new Date(today).toISOString();
      const [appts, slotList, doctorList] = await Promise.all([
        AppointmentAPI.list({ date: isoDate }),
        AppointmentAPI.listSlots({ date: isoDate }),
        DoctorAPI.list({ is_active: true }),
      ]);
      setAppointments(appts);
      setSlots(slotList);
      setDoctors(doctorList);

      // Queue is per-doctor - fetch each active doctor's walk-in queue in
      // parallel and key the result by doctor_id.
      const queueLists = await Promise.all(
        doctorList.map((d) =>
          QueueAPI.list({ doctor_id: d.doctor_id, queue_date: today }).catch(() => [] as QueueEntryListItem[]),
        ),
      );
      const byDoctor: Record<string, QueueEntryListItem[]> = {};
      doctorList.forEach((d, i) => { byDoctor[d.doctor_id] = queueLists[i]; });
      setQueueByDoctor(byDoctor);

      setLastRefreshed(new Date());
    } catch (e: any) {
      if (!silent) toast.error(e.message || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    PatientAPI.getAll().then(setPatients).catch(() => {});
  }, []);

  useEffect(() => {
    fetchAll();
    // Auto-refresh every 30 seconds
    intervalRef.current = setInterval(() => fetchAll(true), 30_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchAll]);

  // Build lookup maps
  const slotMap = useMemo(() => {
    const m: Record<string, AppointmentSlot> = {};
    slots.forEach((s) => { m[s.slot_id] = s; });
    return m;
  }, [slots]);

  const doctorMap = useMemo(() => {
    const m: Record<string, string> = {};
    doctors.forEach((d) => { m[d.doctor_id] = d.name; });
    return m;
  }, [doctors]);

  const patientMap = useMemo(() => {
    const m: Record<string, { name: string; display_id?: string }> = {};
    patients.forEach((p) => {
      if (p.patient_id) {
        m[p.patient_id] = {
          name: p.name ?? `${p.first_name} ${p.last_name}`,
          display_id: p.display_id,
        };
      }
    });
    return m;
  }, [patients]);

  // Scheduled appointments only (source="scheduled") - walk-ins live in
  // queueByDoctor instead, sorted by position rather than slot time.
  const scheduledQueue: ScheduledItem[] = useMemo(() => {
    return appointments
      .filter((a) => a.source === "scheduled")
      .map((a) => {
        const slot = a.slot_id ? slotMap[a.slot_id] : undefined;
        if (!slot) return null;
        return {
          ...a,
          slot_start: slot.start_time,
          slot_end: slot.end_time,
          doctor_name: doctorMap[a.doctor_id] ?? "—",
          patient_name: patientMap[a.patient_id]?.name ?? "Unknown Patient",
          patient_display_id: patientMap[a.patient_id]?.display_id,
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a!.slot_start).getTime() - new Date(b!.slot_start).getTime()) as ScheduledItem[];
  }, [appointments, slotMap, doctorMap, patientMap]);

  const visibleScheduledQueue = useMemo(() => {
    if (!hideTerminal) return scheduledQueue;
    return scheduledQueue.filter((q) => !["completed", "cancelled", "no_show"].includes(q.status));
  }, [scheduledQueue, hideTerminal]);

  // Doctors with at least one non-terminal walk-in today
  const activeWalkinDoctors = useMemo(() => {
    return doctors.filter((d) => {
      const entries = queueByDoctor[d.doctor_id] ?? [];
      return hideTerminal
        ? entries.some((e) => ["waiting", "in_progress", "deferred"].includes(e.state))
        : entries.length > 0;
    });
  }, [doctors, queueByDoctor, hideTerminal]);

  // Stats
  const stats = useMemo(() => {
    const s = { waiting: 0, checked_in: 0, in_progress: 0, completed: 0, cancelled: 0, no_show: 0 };
    scheduledQueue.forEach((q) => {
      if (q.status === "requested" || q.status === "approved" || q.status === "scheduled" || q.status === "confirmed") s.waiting++;
      if (q.status === "in_progress") s.in_progress++;
      if (q.status === "completed") s.completed++;
      if (q.status === "cancelled") s.cancelled++;
      if (q.status === "no_show") s.no_show++;
    });
    return s;
  }, [scheduledQueue]);

  const walkinWaitingCount = useMemo(
    () => Object.values(queueByDoctor).flat().filter((e) => e.state === "waiting").length,
    [queueByDoctor],
  );

  const activeCount = stats.waiting + stats.in_progress;

  return (
    <div className="flex min-h-screen bg-[#f7f8fc]">
      <Sidebar />

      <div className="flex-1 min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Reception Queue" searchMode="both" />
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Receptionist Queue</h1>
              <p className="text-sm text-slate-400 mt-0.5">{fmtLong(new Date())}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">
                Updated {lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
              <button
                onClick={() => setShowRegisterWalkin(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-500 text-white text-sm hover:bg-purple-600 transition shadow-sm font-medium"
              >
                + Register Walk-in
              </button>
              {abdmEnabled && (
                <button
                  onClick={() => setShowScanModal(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-cyan-200 bg-cyan-50 text-sm text-cyan-700 hover:bg-cyan-100 transition shadow-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 3h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  Scan &amp; Share
                </button>
              )}
              <button
                onClick={() => fetchAll()}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 transition shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Stats bar */}
          <div className="flex flex-wrap gap-2 mt-4">
            {[
              { label: "Waiting",       value: stats.waiting,      cls: "text-cyan-700 border-cyan-100 bg-cyan-50" },
              { label: "In Progress",   value: stats.in_progress,  cls: "text-amber-700 border-amber-100 bg-amber-50" },
              { label: "Completed",     value: stats.completed,    cls: "text-green-700 border-green-100 bg-green-50" },
              { label: "No Show",       value: stats.no_show,      cls: "text-red-600 border-red-100 bg-red-50" },
              { label: "Cancelled",     value: stats.cancelled,    cls: "text-slate-600 border-slate-200 bg-slate-50" },
              { label: "Walk-ins Waiting", value: walkinWaitingCount, cls: "text-purple-700 border-purple-100 bg-purple-50" },
            ].map(({ label, value, cls }) => (
              <div key={label} className={`flex items-center gap-2 px-4 py-2 rounded-2xl border ${cls} shadow-sm`}>
                <span className="text-lg font-bold">{value}</span>
                <span className="text-xs font-medium opacity-80">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Toolbar ──────────────────────────────────────────────── */}
        <div className="px-6 pb-3 flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            {activeCount > 0
              ? `${activeCount} scheduled patient${activeCount !== 1 ? "s" : ""} still active`
              : "No active scheduled patients"}
          </p>
          <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideTerminal}
              onChange={(e) => setHideTerminal(e.target.checked)}
              className="rounded border-slate-300 text-cyan-500 focus:ring-cyan-400"
            />
            Hide completed / cancelled
          </label>
        </div>

        {/* ── Walk-in queue ────────────────────────────────────────── */}
        <div className="px-6 pb-6">
          <h2 className="text-base font-bold text-slate-700 mb-3">Walk-in Queue</h2>
          {loading ? (
            <div className="h-20 rounded-2xl bg-slate-100 animate-pulse" />
          ) : activeWalkinDoctors.length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No walk-ins today.</p>
          ) : (
            <div className="space-y-5">
              {activeWalkinDoctors.map((d) => {
                const entries = queueByDoctor[d.doctor_id] ?? [];
                const visible = hideTerminal
                  ? entries.filter((e) => ["waiting", "in_progress", "deferred"].includes(e.state))
                  : entries;
                return (
                  <DoctorWalkinQueue
                    key={d.doctor_id}
                    doctorName={d.name}
                    entries={visible}
                    queueDate={today}
                    onRefresh={() => fetchAll(true)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* ── Scheduled appointments ───────────────────────────────── */}
        <div className="px-6 pb-8 space-y-2">
          <h2 className="text-base font-bold text-slate-700 mb-1">Scheduled Appointments</h2>
          {loading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />
              ))}
            </>
          ) : visibleScheduledQueue.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
              <svg className="w-12 h-12 opacity-25" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-sm">
                {hideTerminal ? "No active patients in queue" : "No appointments for today"}
              </p>
            </div>
          ) : (
            visibleScheduledQueue.map((item, idx) => (
              <ScheduledRow
                key={item.appointment_id}
                item={item}
                queueNum={idx + 1}
                onRefresh={() => fetchAll(true)}
              />
            ))
          )}
        </div>
      </div>

      <RegisterWalkinModal
        isOpen={showRegisterWalkin}
        doctors={doctors}
        onClose={() => setShowRegisterWalkin(false)}
        onRegistered={() => fetchAll(true)}
      />
      <ScanAndShareModal isOpen={showScanModal} onClose={() => setShowScanModal(false)} />
      <ScanArrivalCardStack cards={arrivalCards} onDismiss={dismissArrivalCard} />
    </div>
  );
};

export default ReceptionistQueuePage;
